import React from "react"
import type {
  AiSubject,
  RecommendationApiData,
} from "../../Session/plan-lesson/ai-lesson-planning/aiLessonPlanningTypes"
import { buildStudentReport, heatStyle } from "../studentReportModel"
import { ChevronRight, PillGroup, WarningIcon } from "../reportUi"

export type SubjectState = {
  status: "loading" | "success" | "error"
  data: RecommendationApiData | null
  error: string | null
}

const SUBJECTS: Array<{ key: AiSubject; label: string }> = [
  { key: "math", label: "Maths" },
  { key: "english", label: "English" },
]

const RecommendationsTab: React.FC<{
  results: Partial<Record<AiSubject, SubjectState>>
  firstName: string
  onRetry: () => void
}> = ({ results, firstName, onRetry }) => {
  const [subject, setSubject] = React.useState<AiSubject>("math")
  const [basket, setBasket] = React.useState<string>("all")

  const current = results[subject]
  const report = React.useMemo(
    () =>
      current?.status === "success" && current.data
        ? buildStudentReport(current.data, firstName)
        : null,
    [current, firstName]
  )

  const pickSubject = (next: AiSubject) => {
    setSubject(next)
    setBasket("all")
  }

  const shownBaskets = report
    ? report.baskets.filter(b => basket === "all" || b.key === basket)
    : []

  const matrixColumns = report
    ? `minmax(150px, 1.6fr) repeat(${report.baskets.length}, minmax(64px, 1fr)) 48px`
    : undefined

  return (
    <>
      <div className="sr-head-center">
        <PillGroup options={SUBJECTS} value={subject} onChange={pickSubject} label="Subject" />
        {report ? (
          <span className="sr-muted">
            Data as of <b>{report.asOf}</b> · Generated {report.executedAt}
            {report.isNew ? " · New learner" : ""}
          </span>
        ) : null}
      </div>

      {!current || current.status === "loading" ? (
        <div className="sr-card sr-state">Loading recommendations…</div>
      ) : current.status === "error" ? (
        <div className="sr-card sr-state sr-state-error">
          <div>{current.error}</div>
          <button type="button" className="sr-btn-outline" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : report ? (
        <>
          <section aria-label="Key numbers" className="sr-kpis">
            <div className="sr-card sr-kpi">
              <span className="sr-kpi-label">Recommendations</span>
              <span className="sr-kpi-value">{report.totalCount}</span>
              <span className="sr-kpi-sub">{report.basketLine}</span>
            </div>
            <div className="sr-card sr-kpi">
              <span className="sr-kpi-label">Working level</span>
              <span className="sr-kpi-value">
                {report.yearUsed != null ? `Year ${report.yearUsed}` : "—"}
              </span>
              <span className="sr-kpi-sub">
                Engine estimate{" "}
                {report.inferredYear != null ? `Year ${report.inferredYear}` : "—"}
              </span>
            </div>
            <div className="sr-card sr-kpi">
              <span className="sr-kpi-label">Activities analysed</span>
              <span className="sr-kpi-value">
                {report.historyUsed}{" "}
                <span className="sr-kpi-of">of {report.historyContents}</span>
              </span>
              <span className="sr-kpi-sub">
                {report.historyExcluded} not used by the engine
              </span>
            </div>
            <div className="sr-card sr-kpi">
              <span className="sr-kpi-label">Last activity</span>
              <span className="sr-kpi-value">{report.daysAgo}</span>
              <span className="sr-kpi-sub">{report.lastDate}</span>
            </div>
          </section>

          {report.warnings.length > 0 ? (
            <section aria-label="Warnings" className="sr-warnings">
              {report.warnings.map((w, idx) => (
                <div key={`${idx}:${w}`} className="sr-warning">
                  <WarningIcon />
                  <span>
                    <b>Warning:</b> {w}
                  </span>
                </div>
              ))}
            </section>
          ) : null}

          <div className="sr-grid-2">
            <section aria-label="Placement" className="sr-card sr-section">
              <h2 className="sr-h2">Placement</h2>
              <div className="sr-placement">
                <div className="sr-place-box">
                  <span className="sr-place-label">Profile year</span>
                  <span className="sr-place-value">
                    {report.profileYear != null ? `Y${report.profileYear}` : "—"}
                  </span>
                </div>
                <ChevronRight />
                <div
                  className={`sr-place-box${
                    report.inferredYear !== report.profileYear ? " sr-place-box-warn" : ""
                  }`}
                >
                  <span className="sr-place-label">Engine estimate</span>
                  <span className="sr-place-value">
                    {report.inferredYear != null ? `Y${report.inferredYear}` : "—"}
                  </span>
                </div>
                <ChevronRight />
                <div className="sr-place-box sr-place-box-used">
                  <span className="sr-place-label">Year used</span>
                  <span className="sr-place-value">
                    {report.yearUsed != null ? `Y${report.yearUsed}` : "—"}
                  </span>
                </div>
              </div>
              {report.placementText ? (
                <p className="sr-paragraph">{report.placementText}</p>
              ) : null}
            </section>
          </div>

          <section aria-label="Recommendation mix" className="sr-card sr-section sr-section-mix">
            <div className="sr-stack-4">
              <h2 className="sr-h2">Recommendation mix</h2>
              <span className="sr-muted">
                What the engine is asking {firstName} to do, and why
              </span>
            </div>
            <div className="sr-stack-10">
              <div role="img" aria-label={report.basketLine} className="sr-mix-bar">
                {report.baskets.map((b, i) => (
                  <div
                    key={b.key}
                    title={`${b.label}: ${b.count} items (${b.pct}%)`}
                    style={{
                      flex: `${b.count} 1 0`,
                      background: b.meta.color,
                      borderRadius:
                        report.baskets.length === 1
                          ? "6px"
                          : i === 0
                          ? "6px 0 0 6px"
                          : i === report.baskets.length - 1
                          ? "0 6px 6px 0"
                          : "0",
                    }}
                  />
                ))}
              </div>
              <div className="sr-mix-legend">
                {report.baskets.map(b => (
                  <span key={b.key} className="sr-legend-item">
                    <span className="sr-legend-swatch" style={{ background: b.meta.color }} />
                    <b>{b.label}</b>
                    <span className="sr-legend-count">
                      {b.count} · {b.pct}%
                    </span>
                  </span>
                ))}
                <span className="sr-type-line">{report.typeLine}</span>
              </div>
            </div>

            <div className="sr-grid-mix">
              <div className="sr-stack-10">
                <h3 className="sr-h3">Skill strands covered</h3>
                <div className="sr-matrix-wrap">
                  <div className="sr-matrix" style={{ gridTemplateColumns: matrixColumns }}>
                    <span className="sr-matrix-head sr-matrix-head-left">Strand</span>
                    {report.baskets.map(b => (
                      <span key={b.key} className="sr-matrix-head">
                        {b.label}
                      </span>
                    ))}
                    <span className="sr-matrix-head sr-matrix-head-right">Total</span>
                    {report.matrix.map(row => (
                      <React.Fragment key={row.strand}>
                        <span className="sr-matrix-strand">{row.strand}</span>
                        {row.cells.map(cell => (
                          <span
                            key={cell.basket}
                            className="sr-matrix-cell"
                            style={heatStyle(cell.step)}
                            title={`${row.strand} · ${cell.basket}: ${cell.n}`}
                          >
                            {cell.n ? cell.n : ""}
                          </span>
                        ))}
                        <span className="sr-matrix-total">{row.total}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              <div className="sr-stack-22">
                <div className="sr-stack-10">
                  <div className="sr-level-head">
                    <h3 className="sr-h3">
                      Content level
                      {report.yearUsed != null ? ` vs Year ${report.yearUsed}` : ""}
                    </h3>
                    <span className="sr-level-summary">{report.levelSummary}</span>
                  </div>
                  <div className="sr-level-chart">
                    {report.levelBars.map(l => (
                      <div
                        key={l.year}
                        className="sr-level-col"
                        title={`Year ${l.year}: ${l.n} items${l.isYearUsed ? " (year used)" : ""}`}
                      >
                        <span className="sr-level-count">{l.n ? l.n : ""}</span>
                        <div
                          className={`sr-level-bar${
                            l.isYearUsed ? " sr-level-bar-used" : l.far ? " sr-level-bar-far" : ""
                          }`}
                          style={{ height: `${l.heightPct}px` }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="sr-level-labels">
                    {report.levelBars.map(l => (
                      <span
                        key={l.year}
                        className={`sr-level-label${l.isYearUsed ? " sr-level-label-used" : ""}`}
                      >
                        Y{l.year}
                        {l.isYearUsed ? " ★" : ""}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="sr-stack-10">
                  <h3 className="sr-h3">Most common reasons</h3>
                  {report.reasonBars.length === 0 ? (
                    <span className="sr-muted">No reasons returned.</span>
                  ) : (
                    report.reasonBars.map(q => (
                      <div key={q.label} className="sr-reason-row" title={`${q.label}: ${q.n} items`}>
                        <span className="sr-reason-label">{q.label}</span>
                        <div className="sr-reason-track">
                          <div className="sr-reason-fill" style={{ width: `${q.widthPct}%` }} />
                        </div>
                        <span className="sr-reason-n">{q.n}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </section>

          <section aria-label="Recommended content" className="sr-card sr-section">
            <div className="sr-row">
              <h2 className="sr-h2">Recommended content</h2>
              <div role="group" aria-label="Basket" className="sr-basket-tabs sr-no-print">
                {[
                  { key: "all", label: `All (${report.totalCount})` },
                  ...report.baskets.map(b => ({ key: b.key, label: `${b.label} (${b.count})` })),
                ].map(t => (
                  <button
                    key={t.key}
                    type="button"
                    className="sr-pill"
                    aria-pressed={basket === t.key}
                    onClick={() => setBasket(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {shownBaskets.length === 0 || report.totalCount === 0 ? (
              <span className="sr-muted">No recommendations returned.</span>
            ) : null}
            {shownBaskets.map(b => (
              <div key={b.key} className="sr-basket">
                <div className="sr-basket-head">
                  <span className="sr-basket-tag" style={{ background: b.meta.bg, color: b.meta.fg }}>
                    {b.label}
                  </span>
                  {b.meta.help ? <span className="sr-muted">{b.meta.help}</span> : null}
                </div>
                {b.items.length > 0 ? (
                  <div className="sr-basket-list">
                    {b.items.map(i => (
                      <div key={i.key} className="sr-item">
                        <span className="sr-item-rank">{i.rank}</span>
                        <div className="sr-item-body">
                          <span className="sr-item-title">{i.title}</span>
                          <span className="sr-item-meta">
                            {i.strand} · {i.typeLabel} · <span className="sr-mono">{i.sourceId}</span>
                          </span>
                          {i.reasons.length > 0 ? (
                            <div className="sr-item-reasons">
                              {i.reasons.map((rs, idx) => (
                                <span key={`${idx}:${rs}`} className="sr-item-reason">
                                  {rs}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <div className="sr-item-levels">
                          {i.levels.map((lv, idx) => (
                            <span
                              key={`${idx}:${lv.text}`}
                              className={`sr-item-level${lv.far ? " sr-item-level-far" : ""}`}
                            >
                              {lv.text}
                            </span>
                          ))}
                        </div>
                        <div className="sr-item-score" title={i.score ? `Engine score ${i.score}` : undefined}>
                          <div className="sr-item-score-track">
                            <div className="sr-item-score-fill" style={{ width: `${i.scorePct}%` }} />
                          </div>
                          <span className="sr-item-score-text">{i.score ?? "—"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="sr-muted">No items in this basket.</span>
                )}
              </div>
            ))}
          </section>

          {report.notes.length > 0 ? (
            <section aria-label="Engine notes" className="sr-card sr-notes">
              <h2 className="sr-h3">Engine notes</h2>
              {report.notes.map((n, idx) => (
                <span key={`${idx}:${n}`} className="sr-note">
                  • {n}
                </span>
              ))}
              {report.rulesNote ? (
                <span className="sr-muted">Rule set: {report.rulesNote}</span>
              ) : null}
            </section>
          ) : null}

          <details className="sr-card sr-details">
            <summary>Engine details (admin)</summary>
            <div className="sr-details-grid">
              <span>Engine <b>{report.engine}</b></span>
              <span>Language <b>{report.lang}</b></span>
              <span>Content pool <b>{report.poolSize}</b> items</span>
              <span>Pool built <b>{report.builtAt}</b></span>
              <span>Precompute <b>{report.precomputeMs} ms</b></span>
              <span>Response <b>{report.ms} ms</b></span>
              <span>Learner ID <b className="sr-mono">{report.learnerId || "—"}</b></span>
              <span>Future-dated activities ignored <b>{report.afterAsOf}</b></span>
            </div>
          </details>
        </>
      ) : null}
    </>
  )
}

export default RecommendationsTab
